import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { BILLING_READ_ONLY_EVENT, request, sessionRoleStore, tenantStore, tokenStore, UNAUTHORIZED_EVENT } from './api';
import { setLocale } from './i18n';
import { mfaDeviceStore } from './mfa-device';
import {
  authResponseSchema,
  loginResultSchema,
  sessionSchema,
  type AuthResponse,
  type Session,
  type TenantChoice,
} from './schemas';

/** תוצאת שלב בכניסה: נכנסנו, צריך לבחור עסק, או צריך קוד 2FA. */
export type LoginStep =
  | { kind: 'done' }
  | { kind: 'chooseTenant'; tenants: TenantChoice[]; ticket?: string }
  | { kind: 'mfa'; ticket: string };

export interface MfaVerifyInput {
  ticket: string;
  code?: string;
  recoveryCode?: string;
  rememberDevice: boolean;
}

interface AuthState {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /**
   * מחזיר null בכניסה מוצלחת, או רשימת עסקים לבחירה כשאותו אימייל וסיסמה
   * קיימים ביותר מעסק אחד — ואז קוראים שוב עם `tenant` שנבחר.
   */
  login: (email: string, password: string, tenant?: string) => Promise<LoginStep>;
  /** שלב הקוד: מחליף את כרטיס ה-MFA בסשן. */
  verifyMfa: (input: MfaVerifyInput) => Promise<void>;
  /** החלפת הטוקן אחרי פעולה שמנפיקה סשן חדש (הפעלת 2FA), בלי טעינה מחדש. */
  refreshSession: (res: AuthResponse) => void;
  /** עדכון שדות בסשן אחרי פעולה שלא מנפיקה טוקן (כיבוי 2FA). */
  updateSession: (patch: Partial<Session>) => void;
  /** סשן שהתקבל ממסלול אחר מכניסה — הרשמה שיוצרת עסק (onboarding/signup). */
  adoptSession: (res: AuthResponse) => void;
  /**
   * כניסה עם Google: מחליף את הכרטיס שה-callback החזיר בסשן. עסק אחד → נכנסים;
   * כמה → מחזיר רשימה, והקריאה השנייה עם `tenant` בוחרת.
   */
  loginWithGoogle: (ticket: string, tenant?: string) => Promise<LoginStep>;
  logout: () => void;
  /** בדיקת הרשאה. ההרשאות נאכפות בשרת; כאן זה רק הסתרת UI. */
  can: (...roles: Session['user']['role'][]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  // שחזור סשן מטוקן קיים. השרת הוא המקור לאמת — הטוקן לבדו לא
  // מספיק, כי הוא עלול להיות פג או שהמשתמש הושבת מאז.
  useEffect(() => {
    if (!tokenStore.get()) {
      setStatus('anonymous');
      return;
    }
    const ac = new AbortController();
    request('/auth/me', { schema: sessionSchema, signal: ac.signal })
      .then((s) => {
        // שפת העסק גוברת על מה שנשמר בדפדפן. שינוי — טעינה אחת מחדש.
        if (setLocale(s.tenant.locale)) return;
        setSession(s);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        // ביטול (unmount, או ההרצה הכפולה של StrictMode בפיתוח) אינו כשל
        // אימות. מחיקת הטוקן כאן ניתקה את המשתמש בכל רענון דף בפיתוח —
        // כולל החזרה מ-Google באמצע ההצטרפות.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        tokenStore.clear();
        setStatus('anonymous');
      });
    return () => ac.abort();
  }, []);

  // 401 מכל קריאה שהיא מסיים את הסשן מיד, בלי לחכות לניווט הבא.
  useEffect(() => {
    const onUnauthorized = () => {
      setSession(null);
      setStatus('anonymous');
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  // 402 BILLING_READ_ONLY: המנוי השתנה מאז הכניסה (ניסיון נגמר, תשלום נכשל).
  // מרעננים את בלוק החיוב בסשן כדי שהבאנר יסביר למה הכתיבה נחסמה.
  useEffect(() => {
    const onReadOnly = () => {
      request('/auth/me', { schema: sessionSchema })
        .then((s) => setSession((prev) => (prev ? { ...prev, billing: s.billing ?? null } : prev)))
        .catch(() => undefined);
    };
    window.addEventListener(BILLING_READ_ONLY_EVENT, onReadOnly);
    return () => window.removeEventListener(BILLING_READ_ONLY_EVENT, onReadOnly);
  }, []);

  const adoptSession = useCallback((res: AuthResponse) => {
    tokenStore.set(res.accessToken);
    tenantStore.set(res.tenant.subdomain);
    // הטוקן לא נשמר בסשן עצמו — הוא ב-tokenStore. שמירתו גם ב-state
    // הייתה מציגה אותו ב-React DevTools בלי שום צורך.
    const { accessToken: _token, deviceToken: _device, ...session } = res;
    if (setLocale(session.tenant.locale)) return;
    setSession(session);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (email: string, password: string, tenant?: string): Promise<LoginStep> => {
      const deviceToken = mfaDeviceStore.get(email);
      const res = await request('/auth/login', {
        method: 'POST',
        body: { email, password, ...(deviceToken ? { deviceToken } : {}) },
        // בלי עסק: השרת מוצא אותו לפי האימייל. עם עסק — רק אחרי בחירה.
        tenant: tenant ?? null,
        schema: loginResultSchema,
      });
      if ('chooseTenant' in res) return { kind: 'chooseTenant', tenants: res.chooseTenant };
      if ('mfaRequired' in res) return { kind: 'mfa', ticket: res.mfaTicket };
      adoptSession(res);
      return { kind: 'done' };
    },
    [adoptSession],
  );

  const loginWithGoogle = useCallback(
    async (ticket: string, tenant?: string): Promise<LoginStep> => {
      const deviceToken = mfaDeviceStore.getLast();
      const res = await request('/auth/google/exchange', {
        method: 'POST',
        body: { ticket, ...(tenant ? { subdomain: tenant } : {}), ...(deviceToken ? { deviceToken } : {}) },
        tenant: null,
        schema: loginResultSchema,
      });
      // הכרטיס נשרף בשרת בכל מקרה, ולכן הבחירה בעסק ממשיכה עם החדש שחזר.
      if ('chooseTenant' in res) return { kind: 'chooseTenant', tenants: res.chooseTenant, ticket: res.ticket ?? ticket };
      if ('mfaRequired' in res) return { kind: 'mfa', ticket: res.mfaTicket };
      adoptSession(res);
      return { kind: 'done' };
    },
    [adoptSession],
  );

  const verifyMfa = useCallback(
    async ({ ticket, code, recoveryCode, rememberDevice }: MfaVerifyInput) => {
      const res = await request('/auth/mfa/verify', {
        method: 'POST',
        body: { ticket, rememberDevice, ...(recoveryCode ? { recoveryCode } : { code }) },
        tenant: null,
        schema: authResponseSchema,
      });
      const { deviceToken, ...session } = res;
      if (deviceToken) mfaDeviceStore.set(session.user.email, deviceToken);
      adoptSession(session);
    },
    [adoptSession],
  );

  const refreshSession = useCallback((res: AuthResponse) => {
    tokenStore.set(res.accessToken);
    const { accessToken: _token, deviceToken: _device, ...next } = res;
    setSession(next);
  }, []);

  const updateSession = useCallback((patch: Partial<Session>) => {
    setSession((s) => (s ? { ...s, ...patch } : s));
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    tenantStore.clear();
    setSession(null);
    setStatus('anonymous');
  }, []);

  // התפקיד ל-request() (רשת הביטחון של צפייה בלבד ב-lib/api.ts).
  useEffect(() => {
    sessionRoleStore.set(session?.user.role ?? null);
  }, [session]);

  const can = useCallback(
    (...roles: Session['user']['role'][]) => (session ? roles.includes(session.user.role) : false),
    [session],
  );

  const value = useMemo<AuthState>(
    () => ({ session, status, login, loginWithGoogle, verifyMfa, refreshSession, updateSession, adoptSession, logout, can }),
    [session, status, login, loginWithGoogle, verifyMfa, refreshSession, updateSession, adoptSession, logout, can],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * האם המשתמש רשאי לשנות נתונים (אדמין או עריכה). צפייה בלבד — לא; משמש
 * להסתרת כפתורי כתיבה. ההרשאה עצמה נאכפת בשרת.
 */
export function useCanEdit(): boolean {
  return useAuth().can('OWNER', 'MANAGER');
}
